// ไฟล์: frontend/src/pages/teacher/HealthReports.js
import React, { useState, useEffect } from 'react';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import api from '../../services/api';
import Swal from 'sweetalert2';
import Chart from 'chart.js/auto';

function TeacherHealthReports() {
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [students, setStudents] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [reportMode, setReportMode] = useState('class'); // 'class' หรือ 'student'
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [studentHealthRecords, setStudentHealthRecords] = useState([]);
  const [healthChart, setHealthChart] = useState(null);
  
  useEffect(() => {
    // เริ่มต้นให้เลือกวันที่ย้อนหลัง 30 วัน
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    setEndDate(formatDate(today));
    setStartDate(formatDate(thirtyDaysAgo));
    
    fetchClasses();
  }, []);
  
  useEffect(() => {
    // เมื่อเลือกชั้นเรียนใหม่ ให้โหลดรายชื่อนักเรียนในชั้นเรียนนั้น
    if (selectedClassId) {
      fetchStudentsInClass(selectedClassId);
      fetchHealthRecordSummary();
    }
  }, [selectedClassId, startDate, endDate]);
  
  useEffect(() => {
    // เมื่อเลือกนักเรียนใหม่ ให้โหลดข้อมูลการแปรงฟันและดื่มนมของนักเรียนคนนั้น
    if (reportMode === 'student' && selectedStudentId) {
      fetchStudentHealthRecords();
    }
  }, [reportMode, selectedStudentId, startDate, endDate]);
  
  useEffect(() => {
    // สร้างแผนภูมิสรุป
    if (reportMode === 'class' && summaryData.length > 0) {
      createClassChart();
    } else if (reportMode === 'student' && studentHealthRecords.length > 0) {
      createStudentChart();
    }
    
    // Cleanup function
    return () => {
      if (healthChart) {
        healthChart.destroy();
        setHealthChart(null);
      }
    };
  }, [reportMode, summaryData, studentHealthRecords]);
  
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const fetchClasses = async () => {
    try {
      setIsLoading(true);
      const response = await api.class.getMyClasses();
      const myClasses = response.data.data || [];
      setClasses(myClasses);
      
      // เลือกชั้นเรียนแรกเป็นค่าเริ่มต้น
      if (myClasses.length > 0) {
        setSelectedClassId(myClasses[0].id);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลชั้นเรียนได้',
        confirmButtonColor: '#ff6b01'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchStudentsInClass = async (classId) => {
    try {
      const response = await api.student.getStudentsByClass(classId);
      setStudents(response.data.data || []);
      
      // เลือกนักเรียนคนแรกเป็นค่าเริ่มต้น
      if (response.data.data && response.data.data.length > 0) {
        setSelectedStudentId(response.data.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };
  
  const fetchHealthRecordSummary = async () => {
    if (!selectedClassId || !startDate || !endDate) return;
    
    try {
      setIsLoading(true);
      const response = await api.healthRecord.getHealthRecordSummaryByClass(
        selectedClassId,
        startDate,
        endDate
      );
      setSummaryData(response.data.data || []);
    } catch (error) {
      console.error('Error fetching health record summary:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลสรุปการแปรงฟันและดื่มนมได้',
        confirmButtonColor: '#ff6b01'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchStudentHealthRecords = async () => {
    if (!selectedStudentId || !startDate || !endDate) return;
    
    try {
      setIsLoading(true);
      const response = await api.healthRecord.getHealthRecordsByStudent(
        selectedStudentId,
        startDate,
        endDate
      );
      setStudentHealthRecords(response.data.data || []);
    } catch (error) {
      console.error('Error fetching student health records:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลการแปรงฟันและดื่มนมของนักเรียนได้',
        confirmButtonColor: '#ff6b01'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const createClassChart = () => {
    // หาข้อมูลสำหรับแผนภูมิ
    const totalBrushTeeth = summaryData.reduce((sum, student) => sum + (student.brush_teeth_count || 0), 0);
    const totalDrinkMilk = summaryData.reduce((sum, student) => sum + (student.drink_milk_count || 0), 0);
    const totalDays = summaryData.reduce((sum, student) => sum + (student.total_days || 0), 0);
    
    // สร้างแผนภูมิ
    const ctx = document.getElementById('healthChart');
    
    // ทำลายแผนภูมิเก่า (ถ้ามี)
    if (healthChart) {
      healthChart.destroy();
    }
    
    // สร้างแผนภูมิใหม่
    const newChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: ['แปรงฟัน', 'ดื่มนม'],
        datasets: [{
          label: 'จำนวนครั้งที่บันทึก',
          data: [totalBrushTeeth, totalDrinkMilk],
          backgroundColor: [
            'rgba(54, 162, 235, 0.6)', // สีฟ้า - แปรงฟัน
            'rgba(255, 206, 86, 0.6)'  // สีเหลือง - ดื่มนม
          ],
          borderColor: [
            'rgba(54, 162, 235, 1)',
            'rgba(255, 206, 86, 1)'
          ],
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'จำนวนครั้ง'
            }
          }
        },
        plugins: {
          title: {
            display: true,
            text: 'สรุปการแปรงฟันและดื่มนมทั้งชั้นเรียน'
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                const label = context.dataset.label || '';
                const value = context.raw;
                const total = totalDays * summaryData.length;
                const percentage = Math.round((value / total) * 100);
                return `${label}: ${value} ครั้ง (${percentage}%)`;
              }
            }
          }
        }
      }
    });
    
    setHealthChart(newChart);
  };
  
  const createStudentChart = () => {
    // จัดเรียงข้อมูลตามวันที่
    const sortedRecords = [...studentHealthRecords].sort((a, b) => {
      return new Date(a.record_date) - new Date(b.record_date);
    });
    
    // เตรียมข้อมูลสำหรับแผนภูมิ
    const dates = sortedRecords.map(record => {
      const date = new Date(record.record_date);
      return `${date.getDate()}/${date.getMonth() + 1}`;
    });
    
    const brushTeethData = sortedRecords.map(record => record.brush_teeth ? 1 : 0);
    const drinkMilkData = sortedRecords.map(record => record.drink_milk ? 1 : 0);
    
    // สร้างแผนภูมิ
    const ctx = document.getElementById('healthChart');
    
    // ทำลายแผนภูมิเก่า (ถ้ามี)
    if (healthChart) {
      healthChart.destroy();
    }
    
    // สร้างแผนภูมิใหม่
    const newChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: dates,
        datasets: [
          {
            label: 'แปรงฟัน',
            data: brushTeethData,
            backgroundColor: 'rgba(54, 162, 235, 0.2)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 2,
            fill: false,
            tension: 0.1,
            pointRadius: 5,
            pointHoverRadius: 8
          },
          {
            label: 'ดื่มนม',
            data: drinkMilkData,
            backgroundColor: 'rgba(255, 206, 86, 0.2)',
            borderColor: 'rgba(255, 206, 86, 1)',
            borderWidth: 2,
            fill: false,
            tension: 0.1,
            pointRadius: 5,
            pointHoverRadius: 8
          }
        ]
      },
      options: {
        responsive: true,
        scales: {
          y: {
            beginAtZero: true,
            max: 1,
            ticks: {
              stepSize: 1,
              callback: function(value) {
                return value === 1 ? 'ใช่' : 'ไม่';
              }
            }
          },
          x: {
            ticks: {
              maxRotation: 45,
              minRotation: 45
            }
          }
        },
        plugins: {
          title: {
            display: true,
            text: 'ประวัติการแปรงฟันและดื่มนมรายบุคคล'
          }
        }
      }
    });
    
    setHealthChart(newChart);
  };
  
  // ฟังก์ชันออกรายงาน PDF (จำลอง)
  const exportReport = () => {
    Swal.fire({
      title: 'ออกรายงาน',
      text: 'กำลังเตรียมไฟล์ PDF...',
      icon: 'info',
      timer: 2000,
      showConfirmButton: false
    }).then(() => {
      Swal.fire({
        icon: 'success',
        title: 'ออกรายงานสำเร็จ',
        text: 'รายงานถูกบันทึกลงคอมพิวเตอร์ของคุณแล้ว',
        confirmButtonColor: '#ff6b01'
      });
    });
  };
  
  return (
    <div className="wrapper">
      <Header />
      <Sidebar />
      
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">รายงานการแปรงฟันและดื่มนม</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item">
                    <a href="/teacher/dashboard">หน้าหลัก</a>
                  </li>
                  <li className="breadcrumb-item active">รายงานสุขภาพ</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        
        <section className="content">
          <div className="container-fluid">
            {isLoading ? (
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
            ) : (
              <>
                {/* ตัวเลือกรายงาน */}
                <div className="card card-orange">
                  <div className="card-header">
                    <h3 className="card-title">ตัวเลือกรายงาน</h3>
                  </div>
                  <div className="card-body">
                    {classes.length === 0 ? (
                      <div className="alert alert-warning">
                        <i className="fas fa-exclamation-triangle mr-2"></i>
                        คุณยังไม่ได้รับมอบหมายให้ดูแลชั้นเรียนใดๆ กรุณาติดต่อผู้ดูแลระบบ
                      </div>
                    ) : (
                      <div className="row">
                        <div className="col-md-3">
                          <div className="form-group">
                            <label>ชั้นเรียน</label>
                            <select
                              className="form-control"
                              value={selectedClassId}
                              onChange={(e) => setSelectedClassId(e.target.value)}
                            >
                              {classes.map(classItem => (
                                <option key={classItem.id} value={classItem.id}>
                                  {classItem.class_name} (ห้อง {classItem.room_number})
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-group">
                            <label>ประเภทรายงาน</label>
                            <select
                              className="form-control"
                              value={reportMode}
                              onChange={(e) => setReportMode(e.target.value)}
                            >
                              <option value="class">รายงานทั้งชั้น</option>
                              <option value="student">รายงานรายบุคคล</option>
                            </select>
                          </div>
                        </div>
                        {reportMode === 'student' && (
                          <div className="col-md-3">
                            <div className="form-group">
                              <label>นักเรียน</label>
                              <select
                                className="form-control"
                                value={selectedStudentId}
                                onChange={(e) => setSelectedStudentId(e.target.value)}
                              >
                                {students.map(student => (
                                  <option key={student.id} value={student.id}>
                                    {student.first_name} {student.last_name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        )}
                        <div className="col-md-3">
                          <div className="form-group">
                            <label>วันที่เริ่มต้น</label>
                            <input
                              type="date"
                              className="form-control"
                              value={startDate}
                              onChange={(e) => setStartDate(e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-group">
                            <label>วันที่สิ้นสุด</label>
                            <input
                              type="date"
                              className="form-control"
                              value={endDate}
                              onChange={(e) => setEndDate(e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="col-md-3 d-flex align-items-end">
                          <button
                            className="btn btn-orange"
                            onClick={reportMode === 'class' ? fetchHealthRecordSummary : fetchStudentHealthRecords}
                          >
                            <i className="fas fa-search mr-2"></i>
                            แสดงรายงาน
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* แสดงรายงานทั้งชั้น */}
                {reportMode === 'class' && summaryData.length > 0 && (
                  <div className="row">
                    <div className="col-md-8">
                      <div className="card card-orange">
                        <div className="card-header">
                          <h3 className="card-title">รายงานการแปรงฟันและดื่มนมทั้งชั้น</h3>
                          <div className="card-tools">
                            <button className="btn btn-tool" onClick={exportReport}>
                              <i className="fas fa-file-pdf"></i> ออกรายงาน PDF
                            </button>
                          </div>
                        </div>
                        <div className="card-body">
                          <div className="table-responsive">
                            <table className="table table-bordered table-striped">
                              <thead>
                                <tr>
                                  <th style={{ width: '40px' }}>ลำดับ</th>
                                  <th>ชื่อ-นามสกุล</th>
                                  <th style={{ width: '100px' }}>แปรงฟัน</th>
                                  <th style={{ width: '100px' }}>ดื่มนม</th>
                                  <th style={{ width: '100px' }}>จำนวนวัน</th>
                                  <th style={{ width: '110px' }}>% แปรงฟัน</th>
                                  <th style={{ width: '110px' }}>% ดื่มนม</th>
                                </tr>
                              </thead>
                              <tbody>
                                {summaryData.map((student, index) => {
                                  const totalDays = student.total_days || 0;
                                  const brushTeethPercent = totalDays > 0 ? ((student.brush_teeth_count || 0) / totalDays * 100).toFixed(1) : '0.0';
                                  const drinkMilkPercent = totalDays > 0 ? ((student.drink_milk_count || 0) / totalDays * 100).toFixed(1) : '0.0';
                                  
                                  return (
                                    <tr key={student.student_id}>
                                      <td>{index + 1}</td>
                                      <td>
                                        {student.first_name} {student.last_name}
                                        <small className="d-block text-muted">
                                          รหัส: {student.student_code}
                                        </small>
                                      </td>
                                      <td className="text-center">{student.brush_teeth_count || 0} ครั้ง</td>
                                      <td className="text-center">{student.drink_milk_count || 0} ครั้ง</td>
                                      <td className="text-center">{totalDays} วัน</td>
                                      <td className="text-center">
                                        <span className={`badge ${brushTeethPercent >= 80 ? 'bg-success' : 'bg-danger'}`}>
                                          {brushTeethPercent}%
                                        </span>
                                      </td>
                                      <td className="text-center">
                                        <span className={`badge ${drinkMilkPercent >= 80 ? 'bg-success' : 'bg-danger'}`}>
                                          {drinkMilkPercent}%
                                        </span>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                              <tfoot>
                                <tr className="bg-light">
                                  <th colSpan="2">รวมทั้งชั้น</th>
                                  <th className="text-center">
                                    {summaryData.reduce((sum, student) => sum + (student.brush_teeth_count || 0), 0)} ครั้ง
                                  </th>
                                  <th className="text-center">
                                    {summaryData.reduce((sum, student) => sum + (student.drink_milk_count || 0), 0)} ครั้ง
                                  </th>
                                  <th className="text-center">
                                    {summaryData.reduce((sum, student) => sum + (student.total_days || 0), 0)} วัน
                                  </th>
                                  <th colSpan="2" className="text-center">
                                    {Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24) + 1)} วันที่ตรวจสอบ
                                  </th>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="card card-orange">
                        <div className="card-header">
                          <h3 className="card-title">แผนภูมิการแปรงฟันและดื่มนม</h3>
                        </div>
                        <div className="card-body">
                          <canvas id="healthChart"></canvas>
                        </div>
                      </div>
                      <div className="card card-orange">
                        <div className="card-header">
                          <h3 className="card-title">สรุปการแปรงฟันและดื่มนม</h3>
                        </div>
                        <div className="card-body p-0">
                          <table className="table">
                            <tbody>
                              <tr>
                                <th>จำนวนนักเรียนทั้งหมด</th>
                                <td>{summaryData.length} คน</td>
                              </tr>
                              <tr>
                                <th>ช่วงวันที่ตรวจสอบ</th>
                                <td>
                                  {new Date(startDate).toLocaleDateString('th-TH')} - {new Date(endDate).toLocaleDateString('th-TH')}
                                </td>
                              </tr>
                              <tr>
                                <th>จำนวนวันที่ตรวจสอบ</th>
                                <td>
                                  {Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24) + 1)} วัน
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* แสดงรายงานรายบุคคล */}
                {reportMode === 'student' && studentHealthRecords.length > 0 && (
                  <div className="row">
                    <div className="col-md-8">
                      <div className="card card-orange">
                        <div className="card-header">
                          <h3 className="card-title">
                            รายงานการแปรงฟันและดื่มนมรายบุคคล: {students.find(s => s.id === parseInt(selectedStudentId))?.first_name} {students.find(s => s.id === parseInt(selectedStudentId))?.last_name}
                          </h3>
                          <div className="card-tools">
                            <button className="btn btn-tool" onClick={exportReport}>
                              <i className="fas fa-file-pdf"></i> ออกรายงาน PDF
                            </button>
                          </div>
                        </div>
                        <div className="card-body">
                          <div className="table-responsive">
                            <table className="table table-bordered table-striped">
                              <thead>
                                <tr>
                                  <th style={{ width: '40px' }}>ลำดับ</th>
                                  <th>วันที่</th>
                                  <th>ชั้นเรียน</th>
                                  <th>แปรงฟัน</th>
                                  <th>ดื่มนม</th>
                                  <th>หมายเหตุ</th>
                                  <th>บันทึกโดย</th>
                                </tr>
                              </thead>
                              <tbody>
                                {studentHealthRecords.length === 0 ? (
                                  <tr>
                                    <td colSpan="7" className="text-center">
                                      ไม่พบข้อมูลการแปรงฟันและดื่มนมในช่วงวันที่ที่เลือก
                                    </td>
                                  </tr>
                                ) : (
                                  studentHealthRecords.map((record, index) => (
                                    <tr key={record.id}>
                                      <td>{index + 1}</td>
                                      <td>
                                        {new Date(record.record_date).toLocaleDateString('th-TH', {
                                          year: 'numeric',
                                          month: 'long',
                                          day: 'numeric',
                                        })}
                                      </td>
                                      <td>{record.class_name}</td>
                                      <td>
                                        {record.brush_teeth ? (
                                          <span className="badge bg-success">แปรงแล้ว</span>
                                        ) : (
                                          <span className="badge bg-danger">ไม่ได้แปรง</span>
                                        )}
                                      </td>
                                      <td>
                                        {record.drink_milk ? (
                                          <span className="badge bg-success">ดื่มแล้ว</span>
                                        ) : (
                                          <span className="badge bg-danger">ไม่ได้ดื่ม</span>
                                        )}
                                      </td>
                                      <td>{record.note || '-'}</td>
                                      <td>{record.recorded_by_name}</td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="card card-orange">
                        <div className="card-header">
                          <h3 className="card-title">แผนภูมิการแปรงฟันและดื่มนม</h3>
                        </div>
                        <div className="card-body">
                          <canvas id="healthChart"></canvas>
                        </div>
                      </div>
                      <div className="card card-orange">
                        <div className="card-header">
                          <h3 className="card-title">สรุปการแปรงฟันและดื่มนม</h3>
                        </div>
                        <div className="card-body p-0">
                          <table className="table">
                            <tbody>
                              <tr>
                                <th>จำนวนวันที่แปรงฟัน</th>
                                <td>
                                  {studentHealthRecords.filter(r => r.brush_teeth).length} วัน
                                  ({((studentHealthRecords.filter(r => r.brush_teeth).length / studentHealthRecords.length) * 100).toFixed(1)}%)
                                </td>
                              </tr>
                              <tr>
                                <th>จำนวนวันที่ดื่มนม</th>
                                <td>
                                  {studentHealthRecords.filter(r => r.drink_milk).length} วัน
                                  ({((studentHealthRecords.filter(r => r.drink_milk).length / studentHealthRecords.length) * 100).toFixed(1)}%)
                                </td>
                              </tr>
                              <tr>
                                <th>จำนวนวันที่บันทึกข้อมูล</th>
                                <td>{studentHealthRecords.length} วัน</td>
                              </tr>
                              <tr>
                                <th>ช่วงวันที่ตรวจสอบ</th>
                                <td>
                                  {new Date(startDate).toLocaleDateString('th-TH')} - {new Date(endDate).toLocaleDateString('th-TH')}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>
      
      <Footer />
    </div>
  );
}

export default TeacherHealthReports;